
import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    List, ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Radio,
    RadioGroup,
    FormControlLabel,
    Stack,
    Switch,
    Typography, ButtonGroup, Button
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from '../../../../ui-component/cards/MainCard';
import Transitions from '../../../../ui-component/extended/Transitions';

// assets
import {
    Logout as IconLogout,
    Search as IconSearch,
    Settings as IconSettings,
    Person as IconUser,
    Terminal,
    ManageAccounts, Contrast, EmojiEmotions, Brightness1, LightMode, SettingsBrightness, Brightness3
} from "@mui/icons-material";

// -- Auth
import { useAuth } from "../../../../contexts/AuthContext";
import { SET_THEME_MODE } from '../../../../store/actions';
import { apiGet } from '../../../../api/httpClient';
import {
    getDevImpersonationEmail,
    isDevImpersonationAvailable,
    setDevImpersonationEmail
} from '../../../../auth/devImpersonation';

// config

import config from "../../../../config";

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const { logout, accounts, currentUser, refreshAuthorization } = useAuth();
    const name = accounts[0] && accounts[0].name;
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [sdm, setSdm] = useState(true);
    const [value, setValue] = useState('');
    const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const [devUsers, setDevUsers] = useState([]);
    const [devUserEmail, setDevUserEmail] = useState(getDevImpersonationEmail());
    const [devUsersError, setDevUsersError] = useState('');
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const getSystemTheme = () => (
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    );

    const handleThemeModeChange = (event) => {
        const themeMode = event.target.value;

        localStorage.setItem('themeMode', themeMode);
        dispatch({
            type: SET_THEME_MODE,
            themeMode,
            navType: themeMode === 'system' ? getSystemTheme() : themeMode
        });
    };

    useEffect(() => {
        if (!open || !isDevImpersonationAvailable()) {
            return;
        }

        apiGet('/user/dev/impersonation-users')
            .then((data) => {
                setDevUsers(data || []);
                setDevUsersError('');
            })
            .catch(() => {
                setDevUsers([]);
                setDevUsersError('No se pudieron cargar usuarios locales.');
            });
    }, [open]);

    const handleDevUserChange = async (event) => {
        const email = event.target.value;

        setDevUserEmail(email);
        setDevImpersonationEmail(email);
        await refreshAuthorization();
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        /*color: theme.palette.primary.light,*/
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        /*src={User1}*/
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                    >
                        <EmojiEmotions />
                    </Avatar>
                }
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard sx={{p: 2}} border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ p: 2 }}>
                                        <Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="h4">Hola,</Typography>
                                                <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                    {name}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                        <Box sx={{ p: 2 }}>
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5
                                                    }
                                                }}
                                            >
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 0}
                                                    /*onClick={(event) => handleListItemClick(event, 0, '/user/account-profile/profile1')}*/
                                                >
                                                    <ListItemIcon>
                                                        <ManageAccounts stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Configuración de la Cuenta</Typography>} />
                                                </ListItemButton>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Contrast></Contrast>
                                                    </ListItemIcon>
                                                    <ButtonGroup onClick={handleThemeModeChange} variant={'outlined'}>
                                                        <Button variant={customization.themeMode === 'light'?'contained':'outlined'} value="light" startIcon={<LightMode/>}>Claro</Button>
                                                        <Button variant={customization.themeMode === 'system'?'contained':'outlined'} value="system" startIcon={<SettingsBrightness/>}>Sistema</Button>
                                                        <Button variant={customization.themeMode === 'dark'?'contained':'outlined'} value="dark" startIcon={<Brightness3/>}>Oscuro</Button>
                                                    </ButtonGroup>
                                                </ListItem>
                                                {isDevImpersonationAvailable() && (
                                                    <ListItem alignItems="flex-start">
                                                        <ListItemIcon sx={{ mt: 1 }}>
                                                            <IconUser stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={<Typography variant="body2">Usuario local</Typography>}
                                                            secondary={
                                                                <Stack spacing={1} sx={{ mt: 1 }}>
                                                                    <Box
                                                                        component="select"
                                                                        value={devUserEmail}
                                                                        onChange={handleDevUserChange}
                                                                        sx={{
                                                                            width: '100%',
                                                                            height: 40,
                                                                            borderRadius: 1,
                                                                            border: '1px solid',
                                                                            borderColor: 'divider',
                                                                            backgroundColor: 'background.paper',
                                                                            color: 'text.primary',
                                                                            px: 1,
                                                                            font: 'inherit'
                                                                        }}
                                                                    >
                                                                        <option value="">Microsoft actual</option>
                                                                        {devUserEmail && !devUsers.some((user) => user.email === devUserEmail) && (
                                                                            <option value={devUserEmail}>{devUserEmail}</option>
                                                                        )}
                                                                        {devUsers.map((user) => (
                                                                            <option key={user.id} value={user.email}>
                                                                                {[user.nombre, user.apellidoPaterno].filter(Boolean).join(' ') || user.email}
                                                                            </option>
                                                                        ))}
                                                                    </Box>
                                                                    <Typography variant="caption" color={devUsersError ? 'error' : 'text.secondary'}>
                                                                        {devUsersError || currentUser?.email || 'Sin usuario local'}
                                                                    </Typography>
                                                                </Stack>
                                                            }
                                                        />
                                                    </ListItem>
                                                )}
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 4}
                                                    onClick={logout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Cerrar Sesión</Typography>} />
                                                </ListItemButton>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Terminal stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Ambiente: {config.environment} </Typography> }/>
                                                </ListItem>
                                            </List>
                                        </Box>
                                    </PerfectScrollbar>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
